import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { LogOut, Bell, KeyRound, X, Menu, ChevronDown } from 'lucide-react';
import logoWhiteOrange from '../assets/branco_laranja.png';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { theme } from '../styles/theme';
import { Button, ModalOverlay, ModalBox, ModalHeader, ModalTitle } from './ui';
import { getPasswordError } from '../utils/password';
import api from '../services/api';
import { useTenantName } from '../hooks/useTenantName';
import { cognitoAuth } from '../services/cognito';
import { useToast } from './Toast';

const LANGS = [
  { code: 'pt-BR', label: 'PT', flag: '🇧🇷' },
  { code: 'en',    label: 'EN', flag: '🇺🇸' },
  { code: 'es',    label: 'ES', flag: '🇪🇸' },
];

// Som de notificação — beep simples via Web Audio API
function playNotificationSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 880;
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.4);
  } catch { /* sem suporte a Web Audio */ }
}

const AppHeader = ({ title }) => {
  const { t } = useTranslation();
  const navigate  = useNavigate();
  const location  = useLocation();
  const user      = JSON.parse(localStorage.getItem('user') || 'null');
  const [queueCount, setQueueCount] = useState(0);
  const prevCount = useRef(0);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState('');
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);
  const toast = useToast();

  // Fecha dropdown ao clicar fora
  useEffect(() => {
    const handler = (e) => { if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Extrai tenantId da URL atual
  const tenantIdMatch = location.pathname.match(/\/[^/]+\/([^/]+)/);
  const tenantId = tenantIdMatch?.[1];

  // Polling da fila — apenas para roles que atendem
  const shouldPoll = user && ['agent', 'admin', 'tenant_admin', 'manager'].includes(user.role) && tenantId && !tenantId.startsWith('sallus');

  useEffect(() => {
    if (!shouldPoll) return;

    const poll = async () => {
      try {
        const res = await api.get(`/conversations/${tenantId}/queue`);
        const count = res.data.queue?.length || 0;
        if (count > prevCount.current) playNotificationSound();
        prevCount.current = count;
        setQueueCount(count);
      } catch { /* silencioso */ }
    };

    poll();
    const interval = setInterval(poll, 30000);
    return () => clearInterval(interval);
  }, [tenantId, shouldPoll]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('idToken');
    navigate('/login');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwError('');
    const pwErr = getPasswordError(pwForm.next);
    if (pwErr) { setPwError(pwErr); return; }
    if (pwForm.next !== pwForm.confirm) { setPwError(t('passwordMismatch', { defaultValue: 'As senhas não coincidem' })); return; }
    setPwLoading(true);
    try {
      const result = await cognitoAuth.changePassword(pwForm.current, pwForm.next);
      if (result.success) {
        toast.success(t('passwordChanged', { defaultValue: 'Senha alterada com sucesso!' }));
        setShowChangePassword(false);
        setPwForm({ current: '', next: '', confirm: '' });
      } else {
        setPwError(result.error);
      }
    } catch {
      setPwError(t('genericError', { defaultValue: 'Erro ao alterar senha' }));
    } finally {
      setPwLoading(false);
    }
  };

  const tenantName = useTenantName(tenantId);

  // Título composto: "NomeDaEmpresa · Página"
  const displayTitle = (() => {
    if (!tenantId || tenantId.startsWith('sallus')) return title || 'Libben Agents';
    // tenantName é null enquanto carrega — mostra só o title ou nada
    const tName = tenantName; // null = carregando
    if (!tName) return title || '';
    if (!title) return tName;
    if (title === tName) return tName;
    return `${tName} · ${title}`;
  })();

  if (!user) return null;

  const roleLabels = {
    sallus_admin: t('roles.sallus_admin'),
    admin:        t('roles.admin'),
    manager:      t('roles.manager'),
    agent:        t('roles.agent'),
    team_member:  t('roles.team_member'),
  };

  const goToCallCenter = () => {
    if (tenantId) navigate(`/callcenter/${tenantId}`);
  };

  const currentLang = LANGS.find(l => l.code === i18n.language) || LANGS[0];

  return (
    <Header>
      <Left>
        <HamburgerBtn
          onClick={() => window.dispatchEvent(new Event('sidebar:toggle'))}
          title={t('menu', { defaultValue: 'Menu' })}
          aria-label={t('toggleSidebar', { defaultValue: 'Alternar menu lateral' })}
        >
          <Menu size={20} />
        </HamburgerBtn>
        <LogoImg src={logoWhiteOrange} alt="Libben Agents" />
        <TitleText>{displayTitle}</TitleText>
      </Left>

      <Right>
        {shouldPoll && (
          <NotifBtn onClick={goToCallCenter} title={t('queueTooltip', { count: queueCount })}>
            <Bell size={18} />
            {queueCount > 0 && <NotifBadge>{queueCount > 9 ? '9+' : queueCount}</NotifBadge>}
          </NotifBtn>
        )}

        <UserChip>
          <Avatar>{user.name?.charAt(0)?.toUpperCase() || '?'}</Avatar>
          <UserMeta>
            <UserName>{user.name}</UserName>
            <UserRole>{roleLabels[user.role] || user.role}</UserRole>
          </UserMeta>
        </UserChip>

        <LangDropdown ref={langRef}>
          <LangBtn onClick={() => setLangOpen(v => !v)} $open={langOpen}>
            <span>{currentLang.flag}</span>
            <span>{currentLang.label}</span>
            <ChevronDown size={12} style={{ transition: 'transform 0.2s', transform: langOpen ? 'rotate(180deg)' : 'none' }} />
          </LangBtn>
          {langOpen && (
            <LangMenu>
              {LANGS.map(l => (
                <LangOption
                  key={l.code}
                  $active={l.code === i18n.language}
                  onClick={() => { i18n.changeLanguage(l.code); setLangOpen(false); }}
                >
                  <span>{l.flag}</span>
                  <span>{l.label}</span>
                  {l.code === i18n.language && <LangCheck>✓</LangCheck>}
                </LangOption>
              ))}
            </LangMenu>
          )}
        </LangDropdown>

          <Button variant="ghostLight" size="sm" onClick={() => setShowChangePassword(true)} title={t('changePassword', { defaultValue: 'Trocar senha' })}>
            <KeyRound size={16} />
          </Button>

          <Button variant="ghostLight" size="sm" onClick={handleLogout} title={t('logout')}>
          <LogOut size={16} />
          <span>{t('logout')}</span>
        </Button>
      </Right>

      {showChangePassword && (
        <ModalOverlay onClick={() => setShowChangePassword(false)}>
          <ModalBox onClick={e => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle><KeyRound size={16} /> {t('changePassword', { defaultValue: 'Trocar senha' })}</ModalTitle>
              <CloseBtn onClick={() => setShowChangePassword(false)}><X size={16} /></CloseBtn>
            </ModalHeader>
            <form onSubmit={handleChangePassword}>
              <ModalField>
                <ModalLabel>{t('currentPassword', { defaultValue: 'Senha atual' })}</ModalLabel>
                <ModalInput type="password" value={pwForm.current} onChange={e => setPwForm(p => ({ ...p, current: e.target.value }))} required />
              </ModalField>
              <ModalField>
                <ModalLabel>{t('newPassword', { defaultValue: 'Nova senha' })}</ModalLabel>
                <ModalInput type="password" value={pwForm.next} onChange={e => setPwForm(p => ({ ...p, next: e.target.value }))} placeholder={t('minChars', { defaultValue: 'Mínimo 8 caracteres' })} required />
              </ModalField>
              <ModalField>
                <ModalLabel>{t('confirmPassword', { defaultValue: 'Confirmar nova senha' })}</ModalLabel>
                <ModalInput type="password" value={pwForm.confirm} onChange={e => setPwForm(p => ({ ...p, confirm: e.target.value }))} required />
              </ModalField>
              {pwError && <ModalError>{pwError}</ModalError>}
              <ModalActions>
                <Button type="button" variant="secondary" onClick={() => setShowChangePassword(false)}>{t('cancel', { ns: 'common' })}</Button>
                <Button type="submit" disabled={pwLoading}>{pwLoading ? '...' : t('save', { ns: 'common' })}</Button>
              </ModalActions>
            </form>
          </ModalBox>
        </ModalOverlay>
      )}
    </Header>
  );
};

const HamburgerBtn = styled.button`
  background: rgb(255 255 255 / 0.12);
  border: 1px solid rgb(255 255 255 / 0.2);
  border-radius: ${theme.radii.md};
  width: 36px; height: 36px;
  display: none;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.white};
  cursor: pointer;
  flex-shrink: 0;
  transition: ${theme.transitions.fast};
  &:hover { background: rgb(255 255 255 / 0.22); }
  @media (max-width: 768px) { display: flex; }
`;

const Header = styled.header`
  position: sticky; top: 0; z-index: ${theme.zIndex.sticky};
  background: ${theme.colors.primary};
  padding: 0 ${theme.spacing[6]}; height: 64px;
  display: flex; align-items: center; justify-content: space-between;
  box-shadow: 0 2px 8px rgb(26 73 173 / 0.25);
  @media (max-width: 639px) {
    padding: 0 ${theme.spacing[3]};
  }
`;
const Left = styled.div`display: flex; align-items: center; gap: ${theme.spacing[3]}; min-width: 0;`;
const LogoImg = styled.img`height: 36px; width: auto; object-fit: contain; flex-shrink: 0;`;
const TitleText = styled.h1`
  font-size: ${theme.typography.base}; font-weight: ${theme.typography.semibold};
  color: ${theme.colors.white}; margin: 0;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  @media (max-width: 639px) { display: none; }
`;
const Right = styled.div`display: flex; align-items: center; gap: ${theme.spacing[2]};`;
const NotifBtn = styled.button`
  position: relative; background: rgb(255 255 255 / 0.12);
  border: 1px solid rgb(255 255 255 / 0.2); border-radius: ${theme.radii.md};
  width: 36px; height: 36px; display: flex; align-items: center; justify-content: center;
  color: ${theme.colors.white}; cursor: pointer; transition: ${theme.transitions.fast};
  &:hover { background: rgb(255 255 255 / 0.22); }
  flex-shrink: 0;
`;
const NotifBadge = styled.span`
  position: absolute; top: -4px; right: -4px;
  background: ${theme.colors.danger}; color: white;
  font-size: 10px; font-weight: 700; border-radius: 999px;
  min-width: 16px; height: 16px; padding: 0 3px;
  display: flex; align-items: center; justify-content: center;
  border: 2px solid ${theme.colors.primary};
`;
const UserChip = styled.div`
  display: flex; align-items: center; gap: ${theme.spacing[2]};
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  background: rgb(255 255 255 / 0.12); border: 1px solid rgb(255 255 255 / 0.2);
  border-radius: ${theme.radii.full};
  @media (max-width: 639px) {
    padding: ${theme.spacing[1]} ${theme.spacing[2]};
  }
`;
const Avatar = styled.div`
  width: 28px; height: 28px; border-radius: 50%;
  background: ${theme.colors.secondary}; color: ${theme.colors.white};
  font-size: ${theme.typography.xs}; font-weight: ${theme.typography.bold};
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
`;
const UserMeta = styled.div`
  display: flex; flex-direction: column;
  @media (max-width: 639px) { display: none; }
`;
const UserName = styled.span`
  font-size: ${theme.typography.sm}; font-weight: ${theme.typography.medium};
  color: ${theme.colors.white}; line-height: 1.2;
`;
const UserRole = styled.span`
  font-size: ${theme.typography.xs}; color: rgb(255 255 255 / 0.7); line-height: 1.2;
`;

const LangDropdown = styled.div`
  position: relative;
`;
const LangBtn = styled.button`
  display: flex; align-items: center; gap: 4px;
  background: ${p => p.$open ? 'rgb(255 255 255 / 0.22)' : 'rgb(255 255 255 / 0.12)'};
  border: 1px solid rgb(255 255 255 / 0.2); border-radius: ${theme.radii.md};
  padding: 0 ${theme.spacing[2]}; height: 32px;
  color: ${theme.colors.white}; cursor: pointer; font-size: ${theme.typography.xs};
  font-weight: ${theme.typography.semibold};
  transition: ${theme.transitions.fast};
  &:hover { background: rgb(255 255 255 / 0.22); }
`;
const LangMenu = styled.div`
  position: absolute; top: calc(100% + 6px); right: 0;
  background: white; border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.lg}; box-shadow: ${theme.shadows.lg};
  overflow: hidden; min-width: 100px; z-index: ${theme.zIndex.modal};
`;
const LangOption = styled.button`
  display: flex; align-items: center; gap: ${theme.spacing[2]};
  width: 100%; padding: ${theme.spacing[2]} ${theme.spacing[3]};
  background: ${p => p.$active ? theme.colors.primaryLight : 'white'};
  border: none; cursor: pointer; font-size: ${theme.typography.sm};
  color: ${p => p.$active ? theme.colors.primary : theme.colors.textPrimary};
  font-weight: ${p => p.$active ? theme.typography.semibold : theme.typography.normal};
  transition: ${theme.transitions.fast};
  &:hover { background: ${theme.colors.gray50}; }
`;
const LangCheck = styled.span`
  margin-left: auto; color: ${theme.colors.primary}; font-size: 12px;
`;

const CloseBtn = styled.button`
  background: none; border: none; cursor: pointer;
  color: ${theme.colors.textMuted}; padding: ${theme.spacing[1]};
  &:hover { color: ${theme.colors.textPrimary}; }
`;
const ModalField = styled.div`display: flex; flex-direction: column; gap: ${theme.spacing[1]}; margin-bottom: ${theme.spacing[3]};`;
const ModalLabel = styled.label`font-size: ${theme.typography.sm}; font-weight: ${theme.typography.medium}; color: ${theme.colors.textPrimary};`;
const ModalInput = styled.input`
  padding: ${theme.spacing[2]} ${theme.spacing[3]}; border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md}; font-size: ${theme.typography.sm}; font-family: inherit; outline: none;
  &:focus { border-color: ${theme.colors.primary}; box-shadow: 0 0 0 2px ${theme.colors.primaryLight}; }
`;
const ModalError = styled.div`
  font-size: ${theme.typography.xs}; color: ${theme.colors.danger};
  margin-bottom: ${theme.spacing[3]};
`;
const ModalActions = styled.div`display: flex; gap: ${theme.spacing[3]}; justify-content: flex-end; margin-top: ${theme.spacing[4]};`;

export default AppHeader;
